/**
 * Surprise Results Page - Clay-Inspired Premium UI
 */

import React, { useState, useEffect, useCallback } from 'react';
import { ArrowLeft, Sparkles, MapPin, Calendar, Lock, Loader2, RefreshCw } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { SwoopyWordmark } from '../components/SwoopyLogo';
import PlanStatusBadge from '../components/PlanStatusBadge';
import UpgradeModal from '../components/UpgradeModal';
import { useUser } from '../context/UserContext';
import { useCurrency, CURRENCIES } from '../context/CurrencyContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Convert IDR to USD for API calls (backend expects USD)
const convertIDRToUSD = (amountIDR) => {
  // Using 15,800 IDR = 1 USD exchange rate
  return Math.round(amountIDR * CURRENCIES.USD.rate);
};

// Clay-inspired animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.07, delayChildren: 0.1 }
  }
};

const cardVariants = {
  hidden: { opacity: 0, y: 28, scale: 0.95 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { type: 'spring', stiffness: 260, damping: 20 }
  }
};

const buttonVariants = {
  tap: { scale: 0.96 },
  hover: { scale: 1.02, y: -2 }
};

// Skeleton Loading Component - Clay Style
function TripCardSkeleton() {
  return (
    <div className="swoopy-card p-5">
      <div className="flex items-center justify-between mb-4">
        <div className="swoopy-skeleton h-8 w-24 rounded-full" />
        <div className="swoopy-skeleton h-5 w-16 rounded-lg" />
      </div>
      <div className="swoopy-skeleton h-7 w-44 rounded-lg mb-2" />
      <div className="swoopy-skeleton h-4 w-28 rounded-lg mb-4" />
      <div className="flex gap-2 mb-4">
        <div className="swoopy-skeleton h-8 w-20 rounded-full" />
        <div className="swoopy-skeleton h-8 w-24 rounded-full" />
        <div className="swoopy-skeleton h-8 w-16 rounded-full" />
      </div>
      <div className="pt-4 border-t border-[var(--border-soft)] flex justify-between items-center">
        <div className="swoopy-skeleton h-8 w-20 rounded-lg" />
        <div className="swoopy-skeleton h-11 w-28 rounded-full" />
      </div>
    </div>
  );
}

export default function SurpriseResultsPage({ budget, onSelectTrip, onBack }) {
  const { userId, userStatus, setUserStatus, upgradeToBasic, upgradeToPro } = useUser();
  const { formatPrice } = useCurrency();
  
  const [trips, setTrips] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [gating, setGating] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  const fetchSurprises = useCallback(async (page = 1, append = false) => {
    if (!userId) return;
    
    try {
      if (page === 1) setLoading(true);
      else setLoadingMore(true);
      
      // Convert IDR budget to USD for API (backend expects USD)
      const budgetUSD = convertIDRToUSD(budget);
      
      const response = await fetch(
        `${API_URL}/api/surprises?budget=${budgetUSD}&page=${page}&userId=${userId}`
      );
      
      if (!response.ok) throw new Error('Failed to fetch surprises');
      
      const data = await response.json();
      
      if (append) {
        setTrips(prev => [...prev, ...data.trips]);
      } else {
        setTrips(data.trips);
      }
      
      setPagination(data.pagination);
      setGating(data.gating);
      if (data.userStatus) {
        setUserStatus(data.userStatus);
      }
      setError(null);
      
    } catch (err) {
      console.error('Fetch error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [userId, budget, setUserStatus]);

  useEffect(() => {
    fetchSurprises(1);
  }, [fetchSurprises]);

  const handleLoadMore = () => {
    if (pagination?.hasMore && pagination.nextPage) {
      fetchSurprises(pagination.nextPage, true);
    } else if (gating?.isGated && gating.lockedCount > 0) {
      setShowUpgradeModal(true);
    }
  };

  const handleRegenerate = () => {
    setTrips([]);
    fetchSurprises(1);
  };

  const handleUpgrade = async (planId) => {
    let result;
    if (planId === 'basic') {
      result = await upgradeToBasic();
    } else if (planId === 'pro') {
      result = await upgradeToPro();
    }
    
    if (result) {
      setShowUpgradeModal(false);
      fetchSurprises(1);
    }
  };

  // Format currency using IDR base
  const formatCurrency = (amountUSD) => {
    // Convert USD back to IDR for display (using 15,800 rate)
    const amountIDR = Math.round(amountUSD / CURRENCIES.USD.rate);
    return formatPrice(amountIDR);
  };
  
  // Format budget display (already in IDR)
  const formatBudgetDisplay = () => {
    if (budget >= 1000000) {
      return `Rp${(budget / 1000000).toLocaleString('id-ID')}jt`;
    }
    return `Rp${budget.toLocaleString('id-ID')}`;
  };

  const getVibeStyle = (vibe) => {
    const styles = {
      'Chill': { bg: 'var(--video-teal-soft)', text: 'var(--video-teal)' },
      'Balanced': { bg: 'rgba(255, 183, 77, 0.15)', text: '#F59E0B' },
      'Explore': { bg: 'var(--idea-blue-soft)', text: 'var(--idea-blue)' },
      'Cozy': { bg: 'var(--swoopy-pink-soft)', text: 'var(--swoopy-pink)' },
      'Party': { bg: 'rgba(168, 85, 247, 0.12)', text: '#9333EA' },
    };
    return styles[vibe] || { bg: 'var(--swoopy-pink-soft)', text: 'var(--swoopy-pink)' };
  };

  const showLockedOverlay = gating?.isGated && !pagination?.hasMore && gating.lockedCount > 0;
  const canLoadMore = pagination?.hasMore || showLockedOverlay;

  return (
    <div className="min-h-screen pb-8" style={{ backgroundColor: 'var(--clay-white)' }}>
      {/* Header - Clay Style with Upgrade Button */}
      <header 
        className="sticky top-0 z-40 px-4 py-3"
        style={{ 
          backgroundColor: 'rgba(248, 248, 248, 0.92)',
          backdropFilter: 'blur(16px)',
          WebkitBackdropFilter: 'blur(16px)',
          boxShadow: '0 2px 10px rgba(0,0,0,0.04)',
          borderBottom: '1px solid var(--border-light)'
        }}
      >
        <div className="max-w-lg mx-auto flex items-center justify-between">
          <motion.button 
            onClick={onBack} 
            className="flex items-center gap-2 p-2.5 -ml-2 rounded-xl transition-colors"
            style={{ color: 'var(--text-secondary)' }}
            whileTap={{ scale: 0.95 }}
            whileHover={{ backgroundColor: 'var(--swoopy-pink-soft)' }}
            data-testid="surprise-back-btn"
          >
            <ArrowLeft size={20} strokeWidth={2.5} />
            <span className="text-sm font-bold">Back</span>
          </motion.button>
          
          <div className="flex items-center gap-3">
            <PlanStatusBadge 
              userStatus={userStatus} 
              onClick={() => setShowUpgradeModal(true)}
              compact
            />
            {userStatus?.planType === 'free' && (
              <motion.button
                onClick={() => setShowUpgradeModal(true)}
                className="flex items-center gap-1.5 px-3.5 py-2 rounded-full text-xs font-bold"
                style={{
                  background: 'linear-gradient(145deg, var(--swoopy-pink-light) 0%, var(--swoopy-pink) 50%, var(--swoopy-pink-dark) 100%)',
                  color: 'white',
                  boxShadow: 'var(--shadow-clay-btn)'
                }}
                whileTap={{ scale: 0.95 }}
                whileHover={{ scale: 1.02 }}
                data-testid="header-upgrade-btn"
              >
                <Sparkles size={13} />
                Upgrade
              </motion.button>
            )}
          </div>
        </div>
      </header>

      <div className="max-w-lg mx-auto px-4">
        {/* Results Header */}
        <motion.div 
          className="text-center mb-8 pt-5"
          initial={{ opacity: 0, y: -12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, type: 'spring' }}
        >
          <motion.div 
            className="flex justify-center mb-5"
            animate={{ y: [0, -3, 0] }}
            transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
          >
            <SwoopyWordmark size="lg" />
          </motion.div>
          <h1 style={{ 
            color: 'var(--text-primary)', 
            fontSize: '24px', 
            fontWeight: '600', 
            marginBottom: '8px',
            fontFamily: "'Fredoka', system-ui, sans-serif",
            letterSpacing: '0.01em'
          }}>
            Surprise picks for you
          </h1>
          <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
            {loading ? 'Finding trips...' : 
              `${pagination?.totalAccessible || 0} trips under ${formatBudgetDisplay()}`
            }
          </p>
        </motion.div>

        {/* Loading State */}
        {loading && (
          <motion.div 
            className="space-y-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            {[1, 2, 3].map((i) => (
              <TripCardSkeleton key={i} />
            ))}
          </motion.div>
        )}

        {/* Error State */}
        {error && !loading && (
          <motion.div 
            className="swoopy-card p-8 text-center"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
          >
            <p className="text-base font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
              Oops! Something went wrong.
            </p>
            <motion.button 
              onClick={handleRegenerate}
              className="swoopy-btn h-11 px-6 text-sm"
              variants={buttonVariants}
              whileTap="tap"
            >
              Try again
            </motion.button>
          </motion.div>
        )}

        {/* Trip Cards */}
        {!loading && !error && (
          <>
            {trips.length === 0 ? (
              <motion.div 
                className="swoopy-card p-8 text-center"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                data-testid="no-surprise-trips"
              >
                <p className="text-lg font-bold mb-2" style={{ color: 'var(--text-primary)' }}>
                  No trips found under {formatCurrency(budget)}
                </p>
                <p className="text-sm font-medium mb-4" style={{ color: 'var(--text-muted)' }}>
                  Try increasing your budget
                </p>
                <motion.button 
                  onClick={onBack} 
                  className="swoopy-btn h-11 px-6 text-sm"
                  variants={buttonVariants}
                  whileTap="tap"
                >
                  Adjust budget
                </motion.button>
              </motion.div>
            ) : (
              <motion.div 
                className="space-y-4"
                variants={containerVariants}
                initial="hidden"
                animate="visible"
              >
                <AnimatePresence>
                  {trips.map((trip) => {
                    const vibeStyle = getVibeStyle(trip.vibe);
                    return (
                      <motion.div 
                        key={trip.id}
                        className="swoopy-trip-card p-5 cursor-pointer"
                        onClick={() => onSelectTrip(trip)}
                        variants={cardVariants}
                        whileHover={{ y: -6 }}
                        whileTap={{ scale: 0.98 }}
                        data-testid={`surprise-trip-card-${trip.id}`}
                        layout
                      >
                        {/* Vibe & Duration */}
                        <div className="flex items-center justify-between mb-3">
                          <div 
                            className="swoopy-badge"
                            style={{ backgroundColor: vibeStyle.bg, color: vibeStyle.text }}
                          >
                            <Sparkles size={13} />
                            {trip.vibe}
                          </div>
                          <div className="flex items-center gap-1.5 text-sm font-bold" style={{ color: 'var(--text-secondary)' }}>
                            <Calendar size={15} />
                            {trip.duration}
                          </div>
                        </div>

                        {/* Destination */}
                        <h3 style={{ 
                          fontSize: '19px', 
                          fontWeight: '600', 
                          color: 'var(--text-primary)', 
                          marginBottom: '4px',
                          fontFamily: "'Fredoka', system-ui, sans-serif",
                          letterSpacing: '0.01em'
                        }}>
                          {trip.destination}
                        </h3>
                        <div className="flex items-center gap-1.5 text-sm mb-2" style={{ color: 'var(--text-muted)' }}>
                          <MapPin size={14} />
                          <span className="font-medium">{trip.country}</span>
                        </div>
                        
                        {/* Tagline */}
                        {trip.tagline && (
                          <p 
                            className="text-sm mb-3 italic"
                            style={{ color: 'var(--text-secondary)' }}
                          >
                            "{trip.tagline}"
                          </p>
                        )}

                        {/* Tags */}
                        <div className="flex flex-wrap gap-2 mb-4">
                          {trip.tags.slice(0, 3).map((tag, i) => (
                            <span 
                              key={i}
                              className="px-3 py-1.5 rounded-full text-xs font-medium"
                              style={{ 
                                backgroundColor: 'var(--clay-white)', 
                                color: 'var(--text-secondary)',
                                boxShadow: 'var(--shadow-clay-xs)'
                              }}
                            >
                              {tag}
                            </span>
                          ))}
                          <span 
                            className="px-3 py-1.5 rounded-full text-xs font-medium"
                            style={{ 
                              backgroundColor: trip.pace === 'Packed' ? 'var(--idea-blue-soft)' : 'var(--video-teal-soft)', 
                              color: trip.pace === 'Packed' ? 'var(--idea-blue)' : 'var(--video-teal)'
                            }}
                          >
                            {trip.pace}
                          </span>
                        </div>

                        {/* Cost */}
                        <div 
                          className="flex items-center justify-between pt-4"
                          style={{ borderTop: '2px solid var(--clay-white)' }}
                        >
                          <div>
                            <p className="text-xs font-medium" style={{ color: 'var(--text-muted)' }}>Estimated total</p>
                            <p className="text-xl font-semibold" style={{ color: 'var(--swoopy-pink)' }}>
                              {formatCurrency(trip.cost)}
                            </p>
                          </div>
                          <motion.button 
                            className="swoopy-btn h-11 px-6 text-sm"
                            onClick={(e) => { e.stopPropagation(); onSelectTrip(trip); }}
                            variants={buttonVariants}
                            whileTap="tap"
                            whileHover="hover"
                            data-testid={`surprise-view-btn-${trip.id}`}
                          >
                            Let's go
                          </motion.button>
                        </div>
                      </motion.div>
                    );
                  })}
                </AnimatePresence>
              </motion.div>
            )}

            {/* Load More / Locked Section */}
            {trips.length > 0 && canLoadMore && (
              <motion.div 
                className="mt-6"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
              >
                {showLockedOverlay ? (
                  <motion.div 
                    className="swoopy-card p-8 text-center"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    data-testid="locked-overlay"
                  >
                    <motion.div 
                      className="w-16 h-16 rounded-2xl mx-auto mb-4 flex items-center justify-center"
                      style={{ 
                        background: 'linear-gradient(145deg, var(--swoopy-pink-soft), rgba(233, 116, 143, 0.2))',
                        boxShadow: 'var(--shadow-clay-sm)'
                      }}
                      animate={{ y: [0, -4, 0] }}
                      transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
                    >
                      <Lock size={28} style={{ color: 'var(--swoopy-pink)' }} />
                    </motion.div>
                    <h3 className="text-lg font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>
                      {gating.lockedCount} more surprises waiting
                    </h3>
                    <p className="text-sm mb-5" style={{ color: 'var(--text-muted)' }}>
                      Upgrade to unlock all trips under your budget
                    </p>
                    <motion.button
                      onClick={() => setShowUpgradeModal(true)}
                      className="swoopy-btn h-12 px-8"
                      variants={buttonVariants}
                      whileTap="tap"
                      whileHover="hover"
                      data-testid="unlock-btn"
                    >
                      Unlock More
                    </motion.button>
                  </motion.div>
                ) : (
                  <motion.button
                    onClick={handleLoadMore}
                    disabled={loadingMore}
                    className="w-full h-12 rounded-2xl text-base font-semibold flex items-center justify-center gap-2 transition-all"
                    style={{ 
                      backgroundColor: 'var(--card-bg)',
                      color: 'var(--text-primary)',
                      boxShadow: 'var(--shadow-clay-md)'
                    }}
                    variants={buttonVariants}
                    whileTap="tap"
                    whileHover="hover"
                    data-testid="load-more-btn"
                  >
                    {loadingMore ? (
                      <>
                        <motion.div
                          animate={{ rotate: 360 }}
                          transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                        >
                          <Loader2 size={18} />
                        </motion.div>
                        Loading...
                      </>
                    ) : (
                      <>
                        <Sparkles size={18} style={{ color: 'var(--swoopy-pink)' }} />
                        Load {Math.min(20, pagination.totalAccessible - trips.length)} more
                      </>
                    )}
                  </motion.button>
                )}
              </motion.div>
            )}

            {/* Regenerate button */}
            {trips.length > 0 && !loading && (
              <motion.button
                onClick={handleRegenerate}
                className="w-full mt-4 h-10 rounded-xl text-sm font-medium flex items-center justify-center gap-2 transition-all"
                style={{ color: 'var(--text-muted)' }}
                whileTap={{ scale: 0.98 }}
                whileHover={{ color: 'var(--text-primary)' }}
                data-testid="regenerate-btn"
              >
                <RefreshCw size={14} />
                Shuffle results
              </motion.button>
            )}

            {/* Footer */}
            {trips.length > 0 && (
              <p 
                className="text-center text-xs mt-6 px-4 font-medium"
                style={{ color: 'var(--text-muted)' }}
              >
                Showing {trips.length} of {pagination?.totalAvailable || 0} trips • 
                {userStatus?.isUnlimited ? ' Unlimited access' : ` ${userStatus?.remaining || 0} remaining`}
              </p>
            )}
          </>
        )}
      </div>

      {/* Upgrade Modal */}
      <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        onUpgrade={handleUpgrade}
        lockedCount={gating?.lockedCount || 0}
        planType={userStatus?.planType}
      />
    </div>
  );
}
